<template>
  <Card additional-classes="px-3" v-if="isReservationStatus || isPastStatus">
    <Skeleton v-if="loading" />
    <template v-else-if="error" >
      <p class="error-message">{{ t('errorMessages.getDocuments') }}</p>
    </template>

    <template v-else>
      <div class="p-3">
        <p class="title-4 text-primary-black">{{ t('documents.title') }}</p>

        <!-- No documents message -->
        <p v-if="baseDocuments.length === 0" class="text-muted mt-2">
          {{ t('documents.noDocuments') }}
        </p>

        <!-- List -->
        <div
          v-for="(document, index) in baseDocuments"
          :key="document.id ?? document.fileLink ?? `${document.documentType}-${document.bookingReference}-${index}`"
        >
          <div class="seperator"></div>
          <DocumentComp :document="document" />
        </div>

      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
  // Vue
  import { onMounted, ComputedRef, computed, defineProps, ref } from 'vue'

  // Composables
  import { useBookingStatus } from '@/composables/useBookingStatus'
  import { useCompanyChecks } from '@/composables/useCompanyChecks'

  // Store
  import { useStore } from '@/store'

  // Types
import { BookingSummary, OptionSummary } from '@/services/BookingService.interface'
import { Document, DocumentType } from '@/services/DocumentsService.interface'
import { PrestType } from '@/config'
import { getInsuranceDocumentLink } from '@/config/insuranceDocumentLinks'

  // Components
  import Card from '@/components/UI/Card.vue'
  import DocumentComp from './Document.vue'
  import Skeleton from './DocumentSectionSkeleton.vue'

  // Translations
  import { useI18n } from 'vue-i18n'
  const { t, locale } = useI18n()

  const store = useStore()

  // Store documents as an array instead of a record
  const baseDocuments = ref<Document[]>([])

  // Props
  interface Props {
    booking: BookingSummary | null
  }

  const props = defineProps<Props>()

  // Computed
  const { isReservationStatus, isPastStatus } = useBookingStatus(props.booking)
  const { isCOSOG } = useCompanyChecks()

  const loading: ComputedRef<boolean> = computed(() => {
    return store.getters['document/isLoadingAny']
  })

  const documents: ComputedRef<Document[]> = computed(() => {
    if (props.booking) {
      return store.getters['document/getDocuments'][props.booking.reference]
    }

    return []
  })

  const error = computed(() =>
    store.getters['document/getError']('getDocuments')
  )

  // Methods

  // Generate insurance documents from booking insurance services
  const generateInsuranceDocuments = (): Document[] => {
    if (!props.booking) return []

    const insuranceDocuments: Document[] = []
    
    // Find insurance services in booking options
    const insuranceOptions = props.booking.options.filter(
      (option: OptionSummary) => option.prestTypeId === PrestType.Insurance
    )

    insuranceOptions.forEach((insuranceOption: OptionSummary) => {
      let documentType: DocumentType
      
      // Determine insurance type based on the service name
      if (insuranceOption.name?.toLowerCase().includes('skieur')) {
        documentType = DocumentType.InsuranceSkieur
      } else if (insuranceOption.name?.toLowerCase().includes('annulation') || 
                 insuranceOption.name?.toLowerCase().includes('zen')) {
        documentType = DocumentType.InsuranceAnnulation
      } else {
        // Default to annulation for unknown insurance types
        documentType = DocumentType.InsuranceAnnulation
      }

      // Get the file link from static configuration based on document type and locale
      // TODO The link should come from catlog endpoint 
      const fileLink = getInsuranceDocumentLink(documentType, locale.value)

      // Create insurance document
      insuranceDocuments.push({
        documentType,
        label: insuranceOption.name || t(`documents.${documentType}`),
        fileLink,
        bookingReference: props.booking?.reference || '',
        isActive: true,
        includeInVoucher: false
      })
    })

    return insuranceDocuments
  }

  const BOOKING_CONFIRMATION_TYPES = new Set([
    DocumentType.BookingConfirmation,
    'booking-confirmation-flight',
    'booking-confirmation-train',
  ])

  const createPlaceholderDocument = (documentType: DocumentType): Document => ({
    documentType,
    label: t(`documents.${documentType}`),
    fileLink: '',
    bookingReference: props.booking?.reference || '',
    isActive: true,
    includeInVoucher: false,
  })

  // Ensure vouchers and invoice always appear with availability messaging when not yet returned by the API
  const ensureStandardPlaceholders = (documents: Document[]): Document[] => {
    const result = [...documents]

    if (!result.some(doc => doc.documentType === DocumentType.Vouchers)) {
      const placeholder = createPlaceholderDocument(DocumentType.Vouchers)
      const bookingConfirmationIndex = result.findIndex(doc =>
        BOOKING_CONFIRMATION_TYPES.has(doc.documentType)
      )
      if (bookingConfirmationIndex >= 0) {
        result.splice(bookingConfirmationIndex + 1, 0, placeholder)
      } else {
        result.push(placeholder)
      }
    }

    if (!result.some(doc => doc.documentType === DocumentType.Invoice)) {
      const placeholder = createPlaceholderDocument(DocumentType.Invoice)
      const vouchersIndex = result.findIndex(doc => doc.documentType === DocumentType.Vouchers)
      if (vouchersIndex >= 0) {
        result.splice(vouchersIndex + 1, 0, placeholder)
      } else {
        result.push(placeholder)
      }
    }

    return result
  }

  // Update the base documents with values from the fetched documents and insurance documents
  const enrichBaseDocuments = (fetchedDocs: Document[]): void => {
    if (!Array.isArray(fetchedDocs)) {
      console.error('Fetched documents are not an array:', fetchedDocs)
      return
    }
    
    const insuranceDocuments = generateInsuranceDocuments()
    let allDocuments = ensureStandardPlaceholders(fetchedDocs)
    allDocuments = [...allDocuments, ...insuranceDocuments]
    
        // Apply COGOS-specific modifications. Business Requirement
        if (isCOSOG.value) {
          allDocuments = allDocuments.map(document => {
            if (document.documentType === DocumentType.InsuranceAnnulation) {
              // Use the specific COGOS link from configuration
              const cogosLink = getInsuranceDocumentLink(DocumentType.InsuranceAnnulation, 'fr-FR-CSE-COSOG')
              return {
                ...document,
                fileLink: cogosLink,
                documentType: 'insurance-annulation-cosog' as DocumentType
              }
            }
            return document
          })
        }

    baseDocuments.value = allDocuments
  }
  onMounted(async () => {
    if (props.booking && (isReservationStatus.value || isPastStatus.value)) {
      // Fetch documents using both primary reference and BO reference (if available)
      const primaryReference = props.booking.reference
      const boReference = props.booking.extReferenceBO
      
      // Pass both references to the store action
      await store.dispatch('document/getDocuments', {
        bookingReference: primaryReference,
        boReference: boReference && boReference.trim() !== '' && boReference !== primaryReference
          ? boReference
          : undefined
      })

      enrichBaseDocuments(documents.value || [])
    }
  })
</script>

<style scoped>
.insurance-claim-link {
  font-family: 'Cy';
  font-weight: 700;
  font-size: 14px;
  color: var(--primary-blue);
}
</style>
